<template>
  <div class="chat-input">
    <div class="input-container">
      <input
        v-model="newMessage"
        type="text"
        placeholder="Type your message..."
        class="message-input"
        :disabled="chatBotStore.isLoading"
        @keyup.enter="sendMessageStream"
      />
      <button class="send-btn" :disabled="!newMessage.trim() || chatBotStore.isLoading" @click="sendMessageStream">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M2.17471 2.11991C2.31797 1.9943 2.52232 1.96474 2.69619 2.04373L14.2294 7.0662C14.3999 7.14364 14.51 7.31198 14.5116 7.49784C14.5132 7.68366 14.4062 7.85407 14.2372 7.93436L3.18643 13.4529C3.0194 13.532 2.82137 13.5097 2.67666 13.3953C2.53184 13.2806 2.46566 13.093 2.50674 12.9139L3.6708 7.49784L2.03409 2.62381C1.97626 2.44318 2.03154 2.24553 2.17471 2.11991ZM4.55166 8.01151L3.6708 12.1609L11.8974 8.01151H4.55166ZM4.52334 7.01151H11.7304L3.28701 3.366L4.52334 7.01151Z"
          />
        </svg>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { IChatMessage, IChatRequest, IChunk } from "../interfaces/chatBotInterfaces";
import { useChatBotStore } from "../stores/chatBotStore";

import { ref } from "vue";
import { v4 as createGuid } from "uuid";
import { chatBotApi } from "@/common";

const newMessage = ref("");

const chatBotStore = useChatBotStore();

function copyStringWithoutReference(text: string) {
  return (" " + text).slice(1);
}

async function sendMessageStream() {
  if (!newMessage.value.trim() || chatBotStore.isLoading) return;
  const question = copyStringWithoutReference(newMessage.value.trim());
  newMessage.value = "";

  const userMessage: IChatMessage = {
    id: Date.now().toString(),
    type: "user",
    text: question,
    timestamp: new Date(),
  };

  //Add user message to chat before sending to API to avoid delay in rendering
  chatBotStore.messages.push(userMessage);
  chatBotStore.isLoading = true;

  const request = {
    sessionId: chatBotStore.sessionId,
    message: question,
  } as IChatRequest;

  const response = await chatBotApi.askStream(request);

  const reader = response.body?.getReader();
  const decoder = new TextDecoder();

  if (!reader) {
    console.error("Unable to get response reader");
    chatBotStore.isLoading = false;
    return;
  }

  let buffer: string | undefined = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    const lines = buffer?.split("\n");
    buffer = lines?.pop(); // keep incomplete chunk

    for (const line of lines || []) {
      if (!line.startsWith("data:")) continue;

      const json = line.replace("data:", "").trim();

      if (!json) continue;

      const parsed = JSON.parse(json) as IChunk;
      if (parsed.sessionId) {
        chatBotStore.setSessionId(parsed.sessionId);
      }

      if (parsed.content) {
        const messageText = parsed.content.replace("**", "*"); // replace markdown bold with single asterisk for better formatting in Vue

        if (!chatBotStore.currentChatId) {
          const id = createGuid();
          chatBotStore.currentChatId = id;

          const botMessage: IChatMessage = {
            id: id,
            type: "bot",
            text: messageText,
            timestamp: new Date(),
          };
          chatBotStore.messages.push(botMessage);
        } else {
          // Append to existing bot message
          const currentBotMessage = chatBotStore.messages.find(msg => msg.id === chatBotStore.currentChatId);
          if (currentBotMessage) {
            currentBotMessage.text += messageText;
          }
        }
      }

      if (parsed.done) {
        console.log("stream finished");
        if (parsed.assistantMessageId) {
          console.log(`Updating message ID to ${parsed.assistantMessageId}`);
          const currentBotMessage = chatBotStore.messages.find(msg => msg.id === chatBotStore.currentChatId);
          if (currentBotMessage) {
            currentBotMessage.id = parsed.assistantMessageId.toString();
          }
        }

        chatBotStore.currentChatId = null;
        chatBotStore.isLoading = false;
      }
    }
  }
}
</script>
